import { Bell, BellOff } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDataStore } from '../../stores/useDataStore';
import { User } from '../../types';
import { FollowPopover } from './FollowPopover';

interface FollowButtonProps {
    friend: User;
    user?: User | null;
    className?: string; // Additional classes for the button
    iconSize?: number;
}

export const FollowButton: React.FC<FollowButtonProps> = ({ friend, user, className = "", iconSize = 16 }) => {
    const { currentUser: authUser } = useAuth();
    const currentUser = user !== undefined ? user : authUser; 
    const { follows, followUser, unfollowUser, updateFollowPreset } = useDataStore();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const bellRef = useRef<HTMLButtonElement>(null);

    // If viewing self, render nothing
    if (currentUser?.uid === friend.uid) return null;

    // If no user, we can still render (button will just not work or ideally useAuth handles redirect)
    // But for FriendRow, keeping it visible prevents layout shift/element loss during auth refresh
    if (!currentUser) return null;

    const follow = follows.find(f => f.followedId === friend.uid);
    const isFollowing = !!follow;

    return (
        <>
            <button
                ref={bellRef}
                data-testid={`follow-button-${friend.uid}`}
                onClick={(e) => { e.stopPropagation(); setIsPopoverOpen(true); }}
                className={`${className} transition-colors flex items-center justify-center 
                    ${isFollowing 
                        ? 'text-brand-dark bg-brand-cream-dark/50' 
                        : 'text-brand-dark-light hover:text-brand-dark'
                    }`}
                title={isFollowing ? "Following" : "Follow for updates"}
            >
                {isFollowing ? <Bell size={iconSize} className="fill-brand-dark" /> : <BellOff size={iconSize} />}
            </button>

            <FollowPopover
                friend={friend}
                currentFollow={follow}
                isOpen={isPopoverOpen}
                onClose={() => setIsPopoverOpen(false)}
                anchorRef={bellRef}
                onFollow={(preset) => followUser(currentUser, friend.uid, preset)}
                onUnfollow={() => unfollowUser(currentUser, friend.uid)}
                onUpdatePreset={(preset) => updateFollowPreset(currentUser, friend.uid, preset)}
            />
        </>
    );
};
